import { memo, useMemo, useEffect, useState } from "react";
import { type NodeProps, useUpdateNodeInternals } from "@xyflow/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { AiBrain01Icon, ToolsIcon } from "@hugeicons/core-free-icons";
import { cn } from "@/lib/utils";
import { NodeHandle } from "./NodeHandle";
import { StreamingMarkdown } from "./StreamingMarkdown";
import { AgentStepLog } from "@/components/project/AgentStepLog";

function Spinner() {
  return (
    <svg
      className="size-3 animate-spin text-blue-500"
      viewBox="0 0 16 16"
      fill="none"
    >
      <circle
        cx="8"
        cy="8"
        r="6.5"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="3"
      />
      <path
        d="M14.5 8a6.5 6.5 0 0 0-6.5-6.5"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function FloatingStatus({
  status,
  startedAt,
}: {
  status: NodeStatus | undefined;
  startedAt?: number;
}) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (status !== "running" || !startedAt) {
      setElapsed(0);
      return;
    }
    setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, [status, startedAt]);

  if (!status || status === "idle") return null;

  const label =
    status === "pending"
      ? "Queued"
      : status === "running"
        ? elapsed > 0
          ? `Running... ${elapsed}s`
          : "Running..."
        : status === "completed"
          ? "Done"
          : "Error";

  return (
    <div className="absolute -top-7 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
      <div
        className={cn(
          "flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium shadow-sm border whitespace-nowrap",
          status === "pending" &&
            "bg-yellow-50 dark:bg-yellow-500/10 border-yellow-300/50 dark:border-yellow-500/30 text-yellow-700 dark:text-yellow-400",
          status === "running" &&
            "bg-blue-50 dark:bg-blue-500/10 border-blue-300/50 dark:border-blue-500/30 text-blue-700 dark:text-blue-400",
          status === "completed" &&
            "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-300/50 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400",
          status === "failed" &&
            "bg-red-50 dark:bg-red-500/10 border-red-300/50 dark:border-red-500/30 text-red-700 dark:text-red-400",
        )}
      >
        {status === "running" ? (
          <Spinner />
        ) : (
          <span
            className={cn(
              "size-1.5 rounded-full shrink-0",
              status === "pending" && "bg-yellow-500",
              status === "completed" && "bg-emerald-500",
              status === "failed" && "bg-red-500",
            )}
          />
        )}
        {label}
      </div>
    </div>
  );
}

import {
  useExecutionStore,
  type NodeStatus,
  type AgentStep,
} from "@/lib/stores/execution-store";
import { useModelCatalogStore } from "@/lib/stores/model-catalog-store";
import { PARAM_TYPE_TO_DATA_TYPE } from "@/lib/types/model-catalog";
import { DataType } from "@/lib/types/module";
import type { PipelineNode } from "@/lib/types/pipeline";

const EMPTY_STEPS: AgentStep[] = [];

type HandleItem = {
  name: string;
  label?: string;
  type: DataType;
  required?: boolean;
};

export const AgentNode = memo(function AgentNode({
  data,
  id,
  selected,
}: NodeProps<PipelineNode>) {
  const steps = useExecutionStore((s) => s.agentSteps[id]) ?? EMPTY_STEPS;
  const nodeOutput = useExecutionStore((s) => s.nodeOutputs[id]);
  const nodeStreamingText = useExecutionStore((s) => s.nodeStreamingText[id]);
  const nodeStartedAt = useExecutionStore((s) => s.nodeStartedAt[id]);
  const status: NodeStatus =
    useExecutionStore((s) => s.nodeStatuses[id]) ?? "idle";
  const modelEntry = useModelCatalogStore((s) => {
    const modelId = data.config?.model as string | undefined;
    return modelId ? s.getById(modelId) : undefined;
  });
  const tools: string[] = (data.config?.tools as string[]) ?? [];
  const isRunning = status === "running";
  const isCompleted = status === "completed";
  const updateNodeInternals = useUpdateNodeInternals();

  const handleInputs = useMemo<HandleItem[]>(() => {
    if (!modelEntry) return [];

    // Filter out advanced parameters that should only exist in the settings panel
    const hiddenParams = new Set([
      "temperature",
      "max_tokens",
      "extended_thinking",
      "top_p",
      "top_k",
      "reasoning_effort",
    ]);

    return modelEntry.parameters_schema.params
      .filter((p) => !hiddenParams.has(p.name))
      .map((p) => ({
        name: p.name,
        label: p.label,
        type: PARAM_TYPE_TO_DATA_TYPE[p.type],
        required: p.required,
      }));
  }, [modelEntry]);

  const handleInputSig = handleInputs.map((h) => h.name).join("\0");
  useEffect(() => {
    updateNodeInternals(id);
  }, [handleInputSig, id, updateNodeInternals]);

  // Executor returns { output: str }; fall back to .text for future-proofing
  const outputText =
    nodeStreamingText && nodeStreamingText !== ""
      ? nodeStreamingText
      : ((nodeOutput?.output ?? nodeOutput?.text) as string | undefined);

  return (
    <div className="relative">
      {/* Floating status badge above node */}
      <FloatingStatus status={status} startedAt={nodeStartedAt} />

      {/* Node card */}
      <div
        className={cn(
          "agent-node-bg relative min-w-[220px] max-w-[300px] overflow-hidden rounded-xl border shadow-sm transition-shadow",
          "border-[oklch(0.55_0.25_285/0.25)]",
          status === "running" && "shadow-[0_0_22px_oklch(0.55_0.25_285/0.4)]",
          status === "completed" && "border-green-500/40",
          status === "failed" && "border-destructive/40",
          status === "pending" && "opacity-70",
          selected && "ring-2 ring-brand shadow-lg border-brand/50",
        )}
        style={
          {
            "--grad-dur": isRunning ? "4s" : "10s",
            "--glow-dur": isRunning ? "3s" : "8s",
          } as React.CSSProperties
        }
      >
        {/* Drifting glow orb */}
        <div className="agent-node-glow" />

        {/* Header */}
        <div className="relative flex items-center gap-2 border-b border-[oklch(0.55_0.25_285/0.15)] px-3 py-2.5">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[oklch(0.55_0.25_285/0.15)] dark:bg-white/10">
            <HugeiconsIcon
              icon={AiBrain01Icon}
              size={13}
              className="text-brand dark:text-[oklch(0.80_0.15_285)]"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="truncate text-[13px] font-semibold leading-tight text-foreground dark:text-white/90">
              {data.label}
            </div>
            <div className="text-[10px] font-medium text-brand dark:text-[oklch(0.68_0.15_285)]">
              {modelEntry?.display_name ??
                (data.config?.model as string | undefined) ??
                "Agent"}
            </div>
          </div>
          {/* Tools count badge */}
          {tools.length > 0 && (
            <div className="flex shrink-0 items-center gap-1 rounded-md bg-[oklch(0.55_0.25_285/0.06)] dark:bg-white/5 px-2 py-1 border border-[oklch(0.55_0.25_285/0.12)] dark:border-white/5 shadow-sm">
              <HugeiconsIcon
                icon={ToolsIcon}
                size={13}
                className="text-brand/70 dark:text-[oklch(0.75_0.18_285)]"
              />
              <span className="text-[10px] font-semibold text-brand/80 dark:text-white/70">
                {tools.length}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="relative p-3 space-y-2 min-h-[100px]">
          {/* Running indicator */}
          {isRunning && (
            <div className="flex items-center gap-2 rounded-lg bg-[oklch(0.55_0.25_285/0.10)] dark:bg-white/[0.06] px-2 py-1.5">
              <div className="flex gap-0.5">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-1.5 w-1.5 rounded-full bg-brand dark:bg-[oklch(0.75_0.18_285)] animate-bounce"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
              <span className="text-[11px] font-medium text-brand dark:text-[oklch(0.75_0.18_285)]">
                {steps.length > 0 ? `Step ${steps.length}` : "Thinking…"}
              </span>
            </div>
          )}

          {/* Step log */}
          {steps.length > 0 && (
            <AgentStepLog steps={steps} isRunning={isRunning} />
          )}

          {/* Completed output */}
          {isCompleted && outputText ? (
            <StreamingMarkdown text={outputText} isStreaming={false} />
          ) : !isRunning && !outputText ? (
            <div className="flex flex-col items-center justify-center gap-2 py-6 px-2 text-center">
              <div className="flex items-center justify-center size-8 rounded-full bg-[oklch(0.55_0.25_285/0.06)] dark:bg-white/5 border border-[oklch(0.55_0.25_285/0.1)] dark:border-white/5">
                <HugeiconsIcon
                  icon={AiBrain01Icon}
                  size={16}
                  className="text-brand/50 dark:text-white/40"
                />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-[12px] font-medium text-brand/60 dark:text-white/50">
                  Agent Ready
                </span>
                <span className="text-[10px] text-brand/40 dark:text-white/30 leading-tight">
                  Awaiting input to start reasoning
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Input handles — outside overflow-hidden card, same pattern as ModuleNode */}
      {handleInputs.length > 0 && (
        <div
          className="absolute left-0 top-0 flex flex-col pointer-events-none gap-6 pt-[38px]"
          style={{ width: 0 }}
        >
          {handleInputs.map((input) => (
            <NodeHandle
              key={input.name}
              name={input.name}
              label={input.label}
              type={input.type}
              direction="input"
              required={input.required}
              showLabel={selected}
            />
          ))}
        </div>
      )}

      {/* Output handle */}
      <div
        className="absolute right-0 top-0 flex flex-col pointer-events-none gap-6 pt-[38px]"
        style={{ width: 0 }}
      >
        <NodeHandle
          name="text"
          type={DataType.String}
          direction="output"
          showLabel={selected}
        />
      </div>
    </div>
  );
});
